import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import InputSearchWithSidebar from '../../../common/input-search-with-sidebar/input-search-with-sidebar.component';
import {
  ServicesSubSectionContainer,
  ServicesSubSectionHeader,
} from '../../services/services-subsection/services-subsection.styles';
import { ParcelDetailsSearchInputsContainer } from '../../../routes/parcel-details/parcel-details.styles';
import { useStore } from '../../../stores/store';
import {
  getCityFromPlaceDetails,
  getPredictionsWithUserInput,
  getRegionFromPlaceDetails,
  getZipCodeFromPlaceDetails,
} from '../../../utils/google-places/google-places.utils';
import {
  getShipmentCountriesByFilter,
  getShipmentCountryName,
  getZipCodeDisplayValue,
} from '../../../utils/parcel-creation/parcel-creation-utils';
import { PLACES_API_MINIMAL_REQUEST_LENGTH } from '../../../stores/placesSearchStore';
import {
  VALIDATION_RULE_LATIN_WITH_COMMA,
  VALIDATION_RULE_ONLY_LETTERS,
} from '../../../constants/validation';
import { getSettingReceiverCountry } from '../../../utils/countries/countries.utils';
import { ReactComponent as ArrowRightIcon } from '../../../assets/arrow-right-icon.svg';
import { getPostalCodeLabel } from '../delivery-details-form/delivery-details-form.component';
import { testIds } from '../../../../test-ids';
import { CountryDetails } from '../../../models/shipmentCountryInfo';

const ParcelDetailsDeparture = () => {
  const {
    parcelCreationStore: {
      countryDeparture,
      setDepartureShipmentCountry,
      setDestinationShipmentCountry,
      isLoadingShipmentApi,
      setIsRateSchemaChanged,
      isNextButtonClicked,
      setOmitLimitationsRequest,
      prefillInProgress,
      resetShipmentDataInCaseDeliveryDetailsChanged,
      isDomestic,
    },
    shipmentCountriesStore: {
      senderCountries,
      isDirectionsListApiLoading,
      senderPinnedList,
    },
    placesSearchStore: { getAutocompletePredictions, getPlaceDetails },
    commonStore: { isGuestParcelCreation },
    localizationsStore: { selectedCountry, isLanguageChanging },
    userStore: { user },
  } = useStore();

  const isDefaultFlow =
    getSettingReceiverCountry(countryDeparture?.code, isDomestic) ===
    'default_flow';

  // In case of 'guest' shipping we should limit set of countries to the site-locale
  const availableDepartureCountries = useMemo(
    () =>
      isGuestParcelCreation && selectedCountry
        ? senderCountries.filter((c) => c.code === selectedCountry.country_code)
        : senderCountries,
    [isGuestParcelCreation, selectedCountry, senderCountries]
  );

  const { t } = useTranslation();

  const pinnedCountries: CountryDetails[] = useMemo(
    () =>
      senderPinnedList
        .map((x) => senderCountries.find((y) => y.code === x))
        ?.filter((x): x is CountryDetails => !!x),
    [senderPinnedList, senderCountries]
  );

  const restCountries = useMemo(() => {
    const res =
      availableDepartureCountries.filter(
        (x) => !pinnedCountries.map((y) => y.code).includes(x.code)
      ) ?? [];

    return res;
  }, [pinnedCountries, availableDepartureCountries]);

  useEffect(() => {
    if (isDirectionsListApiLoading) return;

    if (countryDeparture && countryDeparture.zipCode) return;

    if (!countryDeparture && isGuestParcelCreation) {
      const country = availableDepartureCountries[0];
      setDepartureShipmentCountry(country);
      return;
    }

    const canApplyUsersDepartureAddress =
      user?.country &&
      user?.address?.post_code &&
      (!countryDeparture || countryDeparture.code === user.country);

    if (canApplyUsersDepartureAddress) {
      let country = availableDepartureCountries.find(
        (c) => c.code === user.country
      );

      if (country) {
        country = {
          ...country,
          city: user.address?.city ?? '',
          zipCode: user.address?.post_code,
        };
        setDepartureShipmentCountry(country);
      }
      return;
    }

    if (!countryDeparture) {
      const country = availableDepartureCountries
        .filter((c) => c.code === selectedCountry?.country_code)
        ?.at(0);
      if (country) setDepartureShipmentCountry(country);
    }
  }, [
    setDepartureShipmentCountry,
    availableDepartureCountries,
    user,
    isGuestParcelCreation,
    countryDeparture,
    isDirectionsListApiLoading,
    selectedCountry,
  ]);

  const showSkeleton =
    isLoadingShipmentApi ||
    isLanguageChanging ||
    prefillInProgress ||
    isDirectionsListApiLoading;

  return (
    <ServicesSubSectionContainer>
      <ServicesSubSectionHeader>{t('from')}</ServicesSubSectionHeader>
      <ParcelDetailsSearchInputsContainer
        $layout={isDefaultFlow ? 'row' : 'column'}>
        <InputSearchWithSidebar<CountryDetails>
          disabled={!!isGuestParcelCreation}
          name='country_search'
          inputValue={getShipmentCountryName(countryDeparture)}
          defaultOption={countryDeparture}
          placeholder=''
          label={t('country')}
          sidebarTitle={t('country')}
          sidebarInputPlaceholder=''
          displayAllOptionsWithEmptyFilter
          isOptionSelected={(a, b) => a?.code === b?.code}
          getDefaultSearchOptions={(filter) =>
            Promise.resolve(
              getShipmentCountriesByFilter(
                pinnedCountries,
                filter,
                undefined,
                true
              )
            )
          }
          getSearchOptions={(filter) =>
            Promise.resolve(
              getShipmentCountriesByFilter(
                restCountries,
                filter,
                senderPinnedList
              )
            )
          }
          onSearchOptionSelected={(country) => {
            setDepartureShipmentCountry(country);
            setOmitLimitationsRequest(false);
            setDestinationShipmentCountry(null);
            resetShipmentDataInCaseDeliveryDetailsChanged();
          }}
          getKeyForSearchOption={(country) => country.code}
          getDisplayValueForSearchOption={(country) =>
            getShipmentCountryName(country)
          }
          errorMessage={t('this_field_cannot_be_empty')}
          mainInputValidationPredicate={(inputValue) => !!inputValue}
          showSkeleton={showSkeleton}
          validationRule={VALIDATION_RULE_ONLY_LETTERS}
          triggerValidation={isNextButtonClicked.value}
          inputIcon={ArrowRightIcon}
          inputTestId={
            testIds.parceCreation.parcelDetails.departure.countryInput
          }
        />

        <InputSearchWithSidebar<google.maps.places.AutocompletePrediction>
          disabled={!countryDeparture}
          inputValue={
            isDefaultFlow
              ? getZipCodeDisplayValue(countryDeparture)
              : countryDeparture?.zipCode ?? ''
          }
          name='zip_search'
          placeholder=''
          label={getPostalCodeLabel(countryDeparture?.code)}
          sidebarTitle={getPostalCodeLabel(countryDeparture?.code)}
          sidebarInputPlaceholder=''
          debounceSidebarInputChange
          shouldValidateSearchQuery
          getSearchOptions={async (filter) => {
            setOmitLimitationsRequest(false);
            const predictions = await getAutocompletePredictions({
              value: filter ?? '',
              predictionTypes: ['geocode'],
              countryConstraint: countryDeparture?.code.toLocaleLowerCase(),
            });

            return getPredictionsWithUserInput(
              predictions,
              filter,
              PLACES_API_MINIMAL_REQUEST_LENGTH
            );
          }}
          getKeyForSearchOption={(place) => place.place_id}
          getDisplayValueForSearchOption={(place) => place.description}
          onSearchOptionSelected={async (place) => {
            const placeDetails = await getPlaceDetails(
              place,
              undefined,
              undefined,
              true
            );
            if (placeDetails && countryDeparture) {
              const zipCode = getZipCodeFromPlaceDetails(
                placeDetails.address_components
              );
              const city = getCityFromPlaceDetails(
                placeDetails.address_components,
                countryDeparture?.code.toLocaleUpperCase()
              );
              const region = getRegionFromPlaceDetails(
                placeDetails.address_components,
                countryDeparture?.code.toLocaleUpperCase()
              );

              const countryUpdated = {
                ...countryDeparture,
                zipCode: zipCode || '',
                region,
                city,
              };

              setDepartureShipmentCountry(countryUpdated);
              setIsRateSchemaChanged(false);
            } else {
              if (!countryDeparture) return;

              setDepartureShipmentCountry({
                ...countryDeparture,
                zipCode: place.description,
                city: undefined,
              });
            }
            resetShipmentDataInCaseDeliveryDetailsChanged();
          }}
          canModifyInput={false}
          onInputChange={(inputValue) => {
            if (countryDeparture) {
              const zipCode = inputValue;
              setDepartureShipmentCountry({
                ...countryDeparture,
                zipCode,
                city: isDefaultFlow ? countryDeparture.city : undefined,
              });
            }
          }}
          errorMessage={t('this_field_cannot_be_empty')}
          mainInputValidationPredicate={(inputValue) => !!inputValue}
          showSkeleton={showSkeleton}
          validationRule={VALIDATION_RULE_LATIN_WITH_COMMA}
          triggerValidation={isNextButtonClicked.value}
          inputIcon={ArrowRightIcon}
          inputTestId={
            testIds.parceCreation.parcelDetails.departure.postCodeInput
          }
        />
      </ParcelDetailsSearchInputsContainer>
    </ServicesSubSectionContainer>
  );
};

export default observer(ParcelDetailsDeparture);
